import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import intl from "react-intl-universal";
import {
  TbBan,
  TbCirclePlus,
  TbDotsVertical,
  TbFileSearch,
  TbKey,
  TbMailForward,
  TbPlayerPause,
  TbPlayerPlay,
  TbReceipt,
  TbTransfer,
  TbTrash,
  TbWallet,
} from "react-icons/tb";
import { ShortcutCard } from "../../../../../Components/Atoms/ShortcutCard";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useToast } from "../../../../../Hooks/toast";
import {
  PORTAL_DROPDOWN_BACKDROP_Z_INDEX,
  PORTAL_DROPDOWN_Z_INDEX,
  usePortalDropdownPosition,
} from "../../../../../Hooks/usePortalDropdownPosition";
import { useBaasCapabilities } from "../../../../../Hooks/useBaasCapabilities";
import { useBaasPlatformCreateAccess } from "../../../../../Hooks/useBaasPlatformCreateAccess";
import { useBaasInternalTransferOptions } from "../../../../../Hooks/useBaasInternalTransferOptions";
import { formatBaasConsoleConflictMessage } from "../../../../../Services/baasConsoleClient";
import { resendBaasAccountWelcomeEmail, patchBaasAccountLifecycle } from "../../../../../Services/baasAccountsListApi";
import { isBaasExitRailVirtualAccount } from "../../../../../Services/baasVirtualShells";
import {
  canAccountTransact,
  canConsultDigitalProposal,
} from "../../../../../Utils/baasProposalSituation";
import {
  canShowBaasAccountLifecycleActions,
  hasBaasBankingRails,
} from "../../baasCapabilities";
import { BaasAccountCreateModal } from "./BaasAccountCreateModal";
import { BaasAccountProposalModal } from "./BaasAccountProposalModal";
import { BaasAccountTransferModal } from "./BaasAccountTransferModal";
import { isContasOperationShellAccount } from "./BaasEntityVirtualAccountsPanel";

const GerenciarMenu = ({ items }) => {
  const [open, setOpen] = useState(false);
  const { triggerRef, menuRef, menuStyle } = usePortalDropdownPosition(open, 360);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const alignedStyle = (() => {
    if (!menuStyle || !triggerRef.current) return menuStyle;
    const rect = triggerRef.current.getBoundingClientRect();
    return {
      ...menuStyle,
      left: "auto",
      width: "auto",
      minWidth: "14rem",
      maxWidth: "22rem",
      right: Math.max(8, window.innerWidth - rect.right),
    };
  })();

  if (!items.length) return null;

  return (
    <div ref={triggerRef} className="relative inline-flex shrink-0">
      <ShortcutCard
        icon={TbDotsVertical}
        label={intl.get("baas.accounts.detail.manage")}
        active={open}
        title={intl.get("baas.accounts.detail.manage")}
        onClick={() => setOpen((current) => !current)}
      />
      {open && alignedStyle && typeof document !== "undefined"
        ? createPortal(
            <>
              <div
                className="fixed inset-0"
                style={{ zIndex: PORTAL_DROPDOWN_BACKDROP_Z_INDEX }}
                onClick={() => setOpen(false)}
                aria-hidden="true"
              />
              <div
                ref={menuRef}
                role="menu"
                aria-label={intl.get("baas.accounts.detail.manage")}
                style={{ ...alignedStyle, zIndex: PORTAL_DROPDOWN_Z_INDEX }}
                className="overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-brand-dark-border dark:bg-brand-dark-elevated"
              >
                {items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="menuitem"
                      disabled={Boolean(item.disabled)}
                      title={item.title}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium whitespace-normal hover:bg-brand-gold/10 disabled:cursor-not-allowed disabled:opacity-40 ${
                        item.danger
                          ? "text-red-600 dark:text-red-400"
                          : "text-brand-dark dark:text-gray-100"
                      }`}
                      onClick={() => {
                        if (item.disabled) return;
                        setOpen(false);
                        item.onClick?.();
                      }}
                    >
                      {Icon ? <Icon className="h-4 w-4 shrink-0" aria-hidden /> : null}
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </>,
            document.body,
          )
        : null}
    </div>
  );
};

export const BaasAccountDetailQuickActions = ({
  accountId,
  entityId,
  account = null,
  /** Root embeds shell detail — Bloquear/Suspender/Desativar hidden; Criar conta stays Valut-admin. */
  embedded = false,
  onPixKeysClick,
  onCreateVirtualClick = null,
  onProposalLoaded = null,
  onLifecycleChanged = null,
}) => {
  const [createOpen, setCreateOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [proposalOpen, setProposalOpen] = useState(false);
  const [welcomeResending, setWelcomeResending] = useState(false);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const { selectedInstance } = useBaasInstance();
  const { addToast } = useToast();
  const { can, capabilities } = useBaasCapabilities();
  const { isValutConfigAdmin } = useBaasPlatformCreateAccess();
  const canViewPix = can("processing.pix.view");
  const canCreateAccount = can("accounts.create");
  const canTransfer = can("accounts.transfer");
  const { canTransferOut, loading: transferOptionsLoading } =
    useBaasInternalTransferOptions();

  const isVirtual = Boolean(account && isBaasExitRailVirtualAccount(account));
  // Same shell flag as Root / Contas operation shells (incl. explicit role=root).
  const isShell = Boolean(account && isContasOperationShellAccount(account));
  const accountActive = account ? canAccountTransact(account) : true;

  // Banking rails = accounts configured / liberated on the instance.
  // Root: Valut admin only (view-as suppresses isValutConfigAdmin).
  const canLifecycle = canShowBaasAccountLifecycleActions({
    embedded,
    isValutConfigAdmin,
    capabilities,
    canCreateAccount,
  });
  // Consult proposal: Contas keeps partner access; Root is Valut + rails only.
  const canConsultLifecycle = embedded
    ? Boolean(isValutConfigAdmin && hasBaasBankingRails(capabilities))
    : hasBaasBankingRails(capabilities);

  // Transfer enablement comes from instance sources (Root / PaySmart) — not shell balance.
  // Virtual / shell detail: no nested create-virtual; shells already are virtual-like.
  const showCreateOperational = Boolean(account && !isVirtual && canLifecycle);
  const showCreateVirtual = Boolean(
    account && !isVirtual && !isShell && canLifecycle && onCreateVirtualClick,
  );
  // Welcome / conta aberta resend is Valut-admin only (not child-instance admins).
  const showResendWelcome = Boolean(
    account && !isVirtual && !isShell && canLifecycle && isValutConfigAdmin,
  );
  // Shells are instance reserves — no Conta Digital proposal shortcut.
  // Status 4 (aprovada automaticamente) — consult is blocked; pending stays enabled.
  const consultProposalEnabled = canConsultDigitalProposal(account?.proposalSituation);
  const showConsultProposal = Boolean(
    account && !isVirtual && !isShell && entityId && canConsultLifecycle,
  );
  const showPixKeys = Boolean(account && !isShell);
  const transferActionDisabled =
    transferOptionsLoading || !canTransferOut || !accountActive;
  const pixActionDisabled = !canViewPix || !accountActive;

  const handleResendWelcomeEmail = useCallback(async () => {
    if (!selectedInstance || !accountId || welcomeResending) return;

    const confirmed = window.confirm(
      intl.get("baas.accounts.quickActions.resendWelcomeConfirm"),
    );
    if (!confirmed) return;

    setWelcomeResending(true);
    try {
      const result = await resendBaasAccountWelcomeEmail(
        selectedInstance,
        accountId,
      );
      const email = String(result?.email || "").trim();
      addToast({
        type: "success",
        title: email
          ? intl.get("baas.accounts.quickActions.resendWelcomeSuccessTo", {
              email,
            })
          : intl.get("baas.accounts.quickActions.resendWelcomeSuccess"),
      });
    } catch (error) {
      addToast({
        type: "error",
        title: intl.get("baas.accounts.quickActions.resendWelcomeFailed"),
        description: formatBaasConsoleConflictMessage(
          error,
          error?.detail || error?.message || null,
        ),
      });
    } finally {
      setWelcomeResending(false);
    }
  }, [accountId, addToast, selectedInstance, welcomeResending]);

  const lifecycle = String(account?.lifecycle || account?.status || "active").toLowerCase();
  // Root (embedded): never expose Bloquear / Suspender / Desativar / Ativar — keep Criar conta.
  const showLifecycleOps = Boolean(account && canLifecycle && !embedded);
  const canActivate = showLifecycleOps && (lifecycle === "blocked" || lifecycle === "suspended" || lifecycle === "deactivated");
  const canFreeze = showLifecycleOps && lifecycle !== "deactivated";

  const handleLifecycle = useCallback(async (action) => {
    // Root embeds shells — block/suspend/deactivate/activate belong on Contas / Entidades.
    if (embedded || !selectedInstance || !accountId || lifecycleBusy) return;
    if (action === "deactivate") {
      const confirmed = window.confirm(intl.get("baas.accounts.lifecycle.deactivateConfirm"));
      if (!confirmed) return;
    } else if (action !== "activate") {
      const confirmed = window.confirm(intl.get(`baas.accounts.lifecycle.${action}Confirm`));
      if (!confirmed) return;
    }

    setLifecycleBusy(true);
    try {
      try {
        await patchBaasAccountLifecycle(selectedInstance, accountId, {
          action,
          retainBalance: false,
        });
      } catch (err) {
        if (action === "deactivate" && err?.code === "balance_destination_required") {
          const retain = window.confirm(intl.get("baas.accounts.lifecycle.retainBalance"));
          if (!retain) return;
          await patchBaasAccountLifecycle(selectedInstance, accountId, {
            action,
            retainBalance: true,
          });
        } else {
          throw err;
        }
      }
      addToast({
        type: "success",
        title: intl.get("baas.accounts.lifecycle.success"),
      });
      onLifecycleChanged?.();
    } catch (error) {
      addToast({
        type: "error",
        title: intl.get("baas.accounts.lifecycle.failed"),
        description: formatBaasConsoleConflictMessage(
          error,
          error?.detail || error?.message || null,
        ),
      });
    } finally {
      setLifecycleBusy(false);
    }
  }, [accountId, addToast, embedded, lifecycleBusy, onLifecycleChanged, selectedInstance]);

  const actions = useMemo(() => {
    const next = [];

    if (canTransfer) {
      next.push({
        id: "transfer",
        labelKey: "baas.accounts.quickActions.transfer",
        icon: TbTransfer,
        disabled: transferActionDisabled,
        title: !accountActive
          ? intl.get("baas.accounts.quickActions.awaitingApprovalHint")
          : !canTransferOut && !transferOptionsLoading
            ? intl.get("baas.accounts.quickActions.transferDisabledHint")
            : undefined,
        onClick: () => setTransferOpen(true),
      });
    }

    // Instance product gate (pix=0) — show disabled, do not open modal.
    // On virtual: list/link/unlink only — never create new keys.
    // Shells: no Pix keys CTA (not Pix-linkable targets).
    // Pending/rejected proposal — block Pix CTAs until account is active.
    if (showPixKeys) {
      next.push({
        id: "pixKeys",
        labelKey: "baas.accounts.quickActions.pixKeys",
        icon: TbKey,
        disabled: pixActionDisabled,
        title: !accountActive
          ? intl.get("baas.accounts.quickActions.awaitingApprovalHint")
          : !canViewPix
            ? intl.get("baas.accounts.quickActions.pixKeysDisabled")
            : isVirtual
              ? intl.get("baas.accounts.quickActions.pixKeysHintVirtual")
              : intl.get("baas.accounts.quickActions.pixKeysHint"),
        onClick: () => onPixKeysClick?.(),
      });
    }

    next.push({
      id: "transactions",
      labelKey: "baas.accounts.quickActions.transactions",
      icon: TbReceipt,
      to: `/baas/transacoes?accountId=${encodeURIComponent(accountId)}`,
    });

    return next;
  }, [
    accountActive,
    accountId,
    canTransfer,
    canTransferOut,
    canViewPix,
    isVirtual,
    onPixKeysClick,
    pixActionDisabled,
    showPixKeys,
    transferActionDisabled,
    transferOptionsLoading,
  ]);

  const manageActions = useMemo(() => {
    const next = [];

    if (showCreateOperational) {
      next.push({
        id: "create",
        label: intl.get("baas.accounts.quickActions.create"),
        icon: TbCirclePlus,
        onClick: () => setCreateOpen(true),
      });
    }

    if (showCreateVirtual) {
      next.push({
        id: "createVirtual",
        label: intl.get("baas.accounts.quickActions.createVirtual"),
        icon: TbWallet,
        onClick: () => onCreateVirtualClick?.(),
      });
    }

    if (showResendWelcome) {
      next.push({
        id: "resendWelcome",
        label: intl.get("baas.accounts.quickActions.resendWelcome"),
        icon: TbMailForward,
        disabled: welcomeResending,
        title: intl.get("baas.accounts.quickActions.resendWelcomeHint"),
        onClick: () => {
          void handleResendWelcomeEmail();
        },
      });
    }

    if (showConsultProposal) {
      next.push({
        id: "consultProposal",
        label: intl.get("baas.accounts.quickActions.consultProposal"),
        icon: TbFileSearch,
        disabled: !consultProposalEnabled,
        title: consultProposalEnabled
          ? intl.get("baas.accounts.quickActions.consultProposalHint")
          : intl.get("baas.accounts.quickActions.consultProposalDisabled"),
        onClick: () => {
          if (!consultProposalEnabled) return;
          setProposalOpen(true);
        },
      });
    }

    if (canFreeze) {
      next.push(
        {
          id: "block",
          label: intl.get("baas.accounts.lifecycle.block"),
          icon: TbBan,
          disabled: lifecycleBusy || lifecycle === "blocked",
          onClick: () => {
            void handleLifecycle("block");
          },
        },
        {
          id: "suspend",
          label: intl.get("baas.accounts.lifecycle.suspend"),
          icon: TbPlayerPause,
          disabled: lifecycleBusy || lifecycle === "suspended",
          onClick: () => {
            void handleLifecycle("suspend");
          },
        },
        {
          id: "deactivate",
          label: intl.get("baas.accounts.lifecycle.deactivate"),
          icon: TbTrash,
          danger: true,
          disabled: lifecycleBusy,
          onClick: () => {
            void handleLifecycle("deactivate");
          },
        },
      );
    }

    if (canActivate) {
      next.push({
        id: "activate",
        label: intl.get("baas.accounts.lifecycle.activate"),
        icon: TbPlayerPlay,
        onClick: () => {
          void handleLifecycle("activate");
        },
        disabled: lifecycleBusy,
      });
    }

    return next;
  }, [
    canActivate,
    canFreeze,
    consultProposalEnabled,
    handleLifecycle,
    handleResendWelcomeEmail,
    lifecycle,
    lifecycleBusy,
    onCreateVirtualClick,
    showConsultProposal,
    showCreateOperational,
    showCreateVirtual,
    showResendWelcome,
    welcomeResending,
  ]);

  return (
    <section className="min-w-0 w-full">
      <p className="dashboard-section-label mb-3">
        {intl.get("baas.accounts.quickActionsSection")}
      </p>
      <div className="shortcut-scroll-row">
        {actions.map(({ id, labelKey, icon, to, onClick, disabled, title }) => (
          <ShortcutCard
            key={id}
            icon={icon}
            label={intl.get(labelKey)}
            to={to}
            onClick={onClick}
            disabled={disabled === true}
            title={title}
          />
        ))}
        <GerenciarMenu items={manageActions} />
      </div>
      {accountActive && canTransfer && transferActionDisabled && !transferOptionsLoading ? (
        <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.accounts.quickActions.transferDisabledHint")}
        </p>
      ) : null}

      {showCreateOperational ? (
        <BaasAccountCreateModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          initialEntityId={entityId || ""}
        />
      ) : null}
      <BaasAccountTransferModal
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        sourceAccountId={accountId}
      />
      {showConsultProposal && consultProposalEnabled ? (
        <BaasAccountProposalModal
          open={proposalOpen}
          onClose={() => setProposalOpen(false)}
          entityId={entityId}
          onProposalLoaded={onProposalLoaded}
        />
      ) : null}
    </section>
  );
};
